import { Component, effect, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { MenuItem } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { MenuModule } from 'primeng/menu';
import { Authentication } from '../../authentication';
import { User } from "firebase/auth";

@Component({
  selector: 'app-user-menu',
  imports: [ButtonModule, TranslatePipe, MenuModule],
  templateUrl: './user-menu.html',
  styleUrl: './user-menu.css',
})
export class UserMenu {
  translate = inject(TranslateService);
  router = inject(Router);
  authentication = inject(Authentication);

  user = signal<User | null>(this.authentication.currentUser);
  items = signal<MenuItem[]>([]);

  constructor() {
    effect(() => {
      this.user.set(this.authentication.user());
    });

    this.buildMenuItems();
    this.translate.onLangChange.subscribe(() => this.buildMenuItems());
  }

  private buildMenuItems(): void {
    this.translate
      .get(['navbar.userProfile', 'navbar.logout'])
      .subscribe(() => {
        this.items.set([
          { label: this.translate.instant('navbar.userProfile'), command: () => this.router.navigate(['/profile']) },
          { label: this.translate.instant('navbar.logout'), command: () => this.authentication.logout() },
        ]);
      });
  }

  getInitials(user: User): string {
    const trimmedName = user.displayName?.trim();

    if (trimmedName) {
      const parts = trimmedName.split(/\s+/).filter(Boolean);
      if (parts.length >= 2) {
        const initials = parts
          .slice(0, 2)
          .map((part) => part.charAt(0).toUpperCase())
          .join("");
        if (initials.length >= 2) {
          return initials.slice(0, 2);
        }
      }

      if (parts.length === 1) {
        const firstPart = parts[0] ?? '';
        const firstTwo = firstPart.slice(0, 2).toUpperCase();
        if (firstTwo.length === 2) {
          return firstTwo;
        }
        if (firstTwo.length === 1 && user.email) {
          const emailFirst = user.email
            .trim()
            .replace(/[^A-Za-z0-9]/g, "")
            .charAt(0)
            .toUpperCase();
          if (emailFirst) {
            return `${firstTwo}${emailFirst}`.slice(0, 2);
          }
        }
      }
    }

    const emailSource = user.email?.trim().split("@")[0] ?? "";
    const sanitized = emailSource.replace(/[^A-Za-z0-9]/g, "");
    const fallback = sanitized.slice(0, 2).toUpperCase();
    return fallback || "??";
  }
}
